/**
 * This file contains a Vue.js directive that handles image loading failures due to internet disconnection.
 * It stores the source of the image when it fails to load and restores it when the internet connection is restored.
 * It supports both `img` elements and elements with a background image.
 * It also supports Vuetify's `v-img` component by finding the child element with a background image.
 */

let elements = [];

/**
 * This function manages the event listeners and the elements array for a given element.
 * It either adds or removes the event listeners based on the action parameter.
 * It also updates the elements array by either adding or removing the element.
 *
 * @param {HTMLElement} el - The element to manage.
 * @param {string} action - The action to perform. Should be either 'add' or 'remove'.
 */
const manageElement = (el, action) => {
  let bgEl = el.querySelector('.v-image__image');
  
  if (bgEl) {
    bgEl[action + 'EventListener']("load", el.errorListener);
    const index = elements.indexOf(bgEl);
    if (index !== -1) {
      elements[action === 'remove' ? 'splice' : 'push'](index, 1);
    }
  } else if (el.tagName.toLowerCase() === 'img') {
    el[action + 'EventListener']("error", el.errorListener);
    const index = elements.indexOf(el);
    if (index !== -1) {
      elements[action === 'remove' ? 'splice' : 'push'](index, 1);
    }
  }
};

/**
 * This event listener is triggered when the internet connection is restored.
 * It iterates over the elements array and restores the source of each element that had failed to load.
 */
window.addEventListener("online", () => {
  if (navigator.onLine) {
    elements.forEach((el) => {
      if (el.dataset.failedSrc) {
        el.src = el.dataset.failedSrc;
        delete el.dataset.failedSrc;
      }
    });
  }
});

export default {
  /**
   * This function is called when the directive is bound to an element.
   * It sets up an error listener for the element that stores the source of the image when it fails to load.
   * It also adds the element to the elements array.
   *
   * @param {HTMLElement} el - The element the directive is bound to.
   */
  bind(el) {
    el.errorListener = () => {
      if (!navigator.onLine) {
        let bgEl = el.querySelector('.v-image__image');
        
        if (bgEl) {
          let bgUrl = window.getComputedStyle(bgEl).backgroundImage;
          bgUrl = bgUrl.replace('url("','').replace('")','');
          bgEl.dataset.failedSrc = bgUrl;
          elements.push(bgEl);
        } else if (el.tagName.toLowerCase() === 'img') {
          el.dataset.failedSrc = el.src; // Store the failed src
          elements.push(el);
        }
      }
    };
    
    manageElement(el, 'add');
  },
  /**
   * This function is called when the directive is unbound from an element.
   * It removes the error listener from the element and removes the element from the elements array.
   *
   * @param {HTMLElement} el - The element the directive is unbound from.
   */
  unbind(el) {
    manageElement(el, 'remove');
    delete el.errorListener;
  },
};
